import type {
  OverlapEnrollment,
  OverlapPair,
} from '../api/enrollment-overlap.types'

export interface TimelineWindow {
  /** Left edge, 0–100. */
  left: number
  /** Width, 0–100. */
  width: number
}

export interface TimelineRow {
  enrollmentId: string
  label: string
  /** Null when the enrollment has no usable start/end. */
  bar: TimelineWindow | null
  /** Windows where this enrollment overlaps another one. */
  overlaps: TimelineWindow[]
}

export interface Timeline {
  rows: TimelineRow[]
  /** ISO bounds of the time axis; null when nothing could be placed. */
  axisStart: string | null
  axisEnd: string | null
}

const MIN_WIDTH_PERCENT = 0.5

function toMs(value: string | null | undefined): number | null {
  if (!value) return null
  const ms = new Date(value).getTime()
  return Number.isNaN(ms) ? null : ms
}

export function describeEnrollment(enrollment: OverlapEnrollment): string {
  return enrollment.type
    ? `${enrollment.type} · ${enrollment.id}`
    : enrollment.id
}

/**
 * Lays enrollments out on a shared time axis and marks the windows where
 * pairs overlap. Anything with a missing or inverted date is left off the
 * axis (its row is still returned, with `bar: null`).
 */
export function buildTimeline(
  enrollments: OverlapEnrollment[],
  overlaps: OverlapPair[]
): Timeline {
  const ranges = enrollments.map((enrollment) => {
    const start = toMs(enrollment.start)
    const end = toMs(enrollment.end)
    return start !== null && end !== null && end >= start
      ? { start, end }
      : null
  })

  const placed = ranges.filter(
    (range): range is { start: number; end: number } => range !== null
  )
  if (placed.length === 0) {
    return {
      rows: enrollments.map((enrollment) => ({
        enrollmentId: enrollment.id,
        label: describeEnrollment(enrollment),
        bar: null,
        overlaps: [],
      })),
      axisStart: null,
      axisEnd: null,
    }
  }

  const axisStart = Math.min(...placed.map((range) => range.start))
  const axisEnd = Math.max(...placed.map((range) => range.end))
  const span = Math.max(axisEnd - axisStart, 1)

  const toWindow = (start: number, end: number): TimelineWindow => ({
    left: ((start - axisStart) / span) * 100,
    width: Math.max(((end - start) / span) * 100, MIN_WIDTH_PERCENT),
  })

  const rows = enrollments.map((enrollment, index): TimelineRow => {
    const range = ranges[index]
    const windows: TimelineWindow[] = []
    for (const pair of overlaps) {
      if (
        pair.enrollment1Id !== enrollment.id &&
        pair.enrollment2Id !== enrollment.id
      ) {
        continue
      }
      const start = toMs(pair.overlapStart)
      const end = toMs(pair.overlapEnd)
      if (start !== null && end !== null && end >= start) {
        windows.push(toWindow(start, end))
      }
    }
    return {
      enrollmentId: enrollment.id,
      label: describeEnrollment(enrollment),
      bar: range ? toWindow(range.start, range.end) : null,
      overlaps: range ? windows : [],
    }
  })

  return {
    rows,
    axisStart: new Date(axisStart).toISOString(),
    axisEnd: new Date(axisEnd).toISOString(),
  }
}
